import { useCallback, useEffect, useState } from 'react';
import {
  clientesApi,
  type AtualizarClienteRequest,
  type ClienteResumo,
} from '@/features/clientes/api/clientesApi';
import { useSyncReload } from '@/shared/hooks/useSyncReload';

type Estado = {
  clientes: ClienteResumo[];
  carregando: boolean;
  erro: string | null;
};

export function useClientes() {
  const [estado, setEstado] = useState<Estado>({
    clientes: [],
    carregando: false,
    erro: null,
  });

  const recarregar = useCallback(() => {
    setEstado((s) => ({ ...s, carregando: true, erro: null }));
    return clientesApi.listar().then(
      (clientes) => setEstado({ clientes, carregando: false, erro: null }),
      (e: unknown) => {
        const mensagem = e instanceof Error ? e.message : 'Erro desconhecido';
        setEstado((s) => ({ ...s, carregando: false, erro: mensagem }));
      },
    );
  }, []);

  const atualizar = useCallback(
    async (id: string, dados: AtualizarClienteRequest) => {
      await clientesApi.atualizar(id, dados);
      void recarregar();
    },
    [recarregar],
  );

  const excluir = useCallback(
    async (id: string) => {
      setEstado((s) => ({
        ...s,
        clientes: s.clientes.filter((c) => c.id !== id),
      }));
      try {
        await clientesApi.excluir(id);
      } finally {
        void recarregar();
      }
    },
    [recarregar],
  );

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  // Recarrega quando o push confirma sync de qualquer entidade — assim
  // o badge ⏳ "Aguardando servidor" some sozinho dos itens recém-criados.
  useSyncReload(recarregar);

  return { ...estado, recarregar, atualizar, excluir };
}
